import { useEffect, useState } from 'react'
import { useAbruf } from './Abruf'
import { api } from './api'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { meldeErfolg } from './Erfolgsstelle'
import { fachlicherSatz } from './Fehlersatz'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import { t } from './sprache'
import { Blattabschnitt } from './Stammdaten'

/**
 * **Die Kontengruppe eines Kontos — geändert ab einem Monat oder immer**
 * (2.1 Abschnitt 3.3, R1648, R1649).
 *
 * Die Gruppe gehört zur Zuordnung und gilt ab demselben Monat wie sie. Das
 * Fenster zeigt die, die heute gilt — die der offenen Zuordnung — und ändert
 * sie mit derselben Wahl wie beim Umhängen: **ab Monat**, vorgeschlagen der
 * des Berichts, **oder immer**. Beispiel: ein Bankkonto ab Oktober in
 * *Liquide Mittel* zählt ab Oktober zum Finanzmittelfonds; die Monate davor
 * bleiben.
 */
export function Kontogruppe({
  konto,
  periode,
  aufAenderung,
}: {
  konto: string
  periode?: number
  aufAenderung?: () => void
}) {
  const darf = useDarf()
  const gruppen = useAbruf(() => api.stammdatenliste('kontengruppe'), [])
  const zuordnungen = useAbruf(() => api.stammdatenliste('kontozuordnung'), [konto])
  const [wahl, setWahl] = useState('')
  const [immer, setImmer] = useState(false)
  const [ab, setAb] = useState<number | null>(periode ?? null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  // Die heutige Gruppe: die der offenen Zuordnung, sonst der jüngsten.
  const eigene = (zuordnungen.wert?.eintraege ?? []).filter((z) => z.werte['konto'] === konto)
  const offen = eigene.find((z) => !z.werte['gueltig_bis']) ?? eigene.at(-1)
  const heute = offen?.werte['gruppe'] ?? ''

  useEffect(() => {
    setWahl(heute)
  }, [heute])

  if (!darf('kontogruppeSetzen')) {
    return null
  }

  async function uebernehmen() {
    if (!wahl || wahl === heute) {
      return
    }
    if (!immer && ab === null) {
      setMeldung(t('positionskonten.ohneperiode'))
      return
    }
    setLaeuft(true)
    setMeldung(null)
    try {
      await api.kontogruppeSetzen(konto, wahl, ab ?? 0, immer)
      meldeErfolg(t('erfolg.kontogruppe.gesetzt'))
      zuordnungen.erneut()
      aufAenderung?.()
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Blattabschnitt kennung="eigenschaften.kontogruppe" titel={t('kontogruppe.titel')}>
      <div className="kontogruppe">
        <Auswahlfeld
          wert={wahl}
          leer="—"
          eintraege={(gruppen.wert?.eintraege ?? []).map(ausStammdatum)}
          abgeschaltet={laeuft}
          beschriftung={t('kontogruppe.titel')}
          aufWahl={setWahl}
        />
        <fieldset className="wirkungswahl">
          <legend>{t('kontogruppe.wirkung')}</legend>
          <span className="wirkungab">
            <input
              type="radio"
              name="kontogruppe-wirkung"
              aria-label={t('positionskonten.ab')}
              checked={!immer}
              onChange={() => setImmer(false)}
            />
            <Periodenfeld wert={ab} aufWert={setAb} gesperrt={immer} beschriftung={t('positionskonten.ab')} klasse="" />
          </span>
          <label>
            <input type="radio" name="kontogruppe-wirkung" checked={immer} onChange={() => setImmer(true)} />
            {t('positionskonten.immer')}
          </label>
        </fieldset>
        <button type="button" disabled={laeuft || !wahl || wahl === heute} onClick={() => void uebernehmen()}>
          {t('kontogruppe.uebernehmen')}
        </button>
      </div>
      {meldung && <p className="hinweis">{meldung}</p>}
    </Blattabschnitt>
  )
}
